import { SparklesIcon } from "@heroicons/react/24/outline";

import Modal from "../../../layouts/Modal";
import { ISerpApiSearchResult } from "../../../models/PartLookup";
import PriceFormat from "../../../utils/priceFormat";
import { summarisePrices } from "../../../utils/intelligencePricing";

/**
 * The eBay listings behind a Wilma Intelligence price. Live asking prices only: the sold-listings
 * search was switched off upstream. Also used by the Initial Price Estimate card for one category.
 */
export default function UnlistedIntelligenceModal({
  open,
  setOpen,
  results,
  loading,
  title = "Wilma Intelligence — eBay matches",
  searchTerm,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  results: ISerpApiSearchResult[] | null;
  loading: boolean;
  title?: string;
  /** Shown above the list so it is clear what was searched. */
  searchTerm?: string;
}) {
  const summary = summarisePrices(results);
  const count = results?.length ?? 0;

  return (
    <Modal open={open} setOpen={setOpen} width={"max-w-2xl"}>
      <div className="flex items-center gap-2 border-b pb-3">
        <SparklesIcon className="h-5 w-5 text-amber-500" aria-hidden="true" />
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="mt-4 space-y-2">
        {searchTerm && (
          <p className="px-2 text-xs text-gray-500">
            Searched for <span className="font-medium text-gray-700">“{searchTerm}”</span>
          </p>
        )}
        <p className="px-2 text-sm text-gray-500">
          {loading
            ? "Looking up live eBay listings…"
            : `There ${count === 1 ? "is" : "are"} ${count} active listing${count === 1 ? "" : "s"}${
                summary ? ` with an average asking price of ${PriceFormat(summary.avg)}` : ""
              }.`}
        </p>
        <IntelligenceList loading={loading} results={results} />
      </div>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
          onClick={() => setOpen(false)}
        >
          Close
        </button>
      </div>
    </Modal>
  );
}

function IntelligenceList({
  loading,
  results,
}: {
  loading: boolean;
  results: ISerpApiSearchResult[] | null;
}) {
  return (
    <div className="flex max-h-[60vh] flex-col rounded-lg border border-gray-200">
      <div className="flex items-center justify-between border-b bg-gray-50 px-3 py-2">
        <h4 className="text-sm font-semibold text-gray-800">Active listings</h4>
        <span className="text-xs text-gray-500">
          {results == null ? "" : `${results.length} listing${results.length === 1 ? "" : "s"}`}
        </span>
      </div>
      <div className="overflow-y-auto">
        {loading ? (
          <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
        ) : results == null || results.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">No results</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {results.map((r, idx) => (
              <li key={`${r.link}-${idx}`} className="p-2">
                <a
                  href={r.link}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-start gap-3 rounded-md p-1 hover:bg-gray-50"
                >
                  {r.thumbnail ? (
                    <img
                      src={r.thumbnail}
                      alt=""
                      className="h-14 w-14 flex-shrink-0 rounded object-cover ring-1 ring-gray-200"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-14 w-14 flex-shrink-0 rounded bg-gray-100 ring-1 ring-gray-200" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-xs text-gray-800">{r.title}</p>
                    {r.condition && (
                      <p className="mt-0.5 text-[11px] text-gray-500">{r.condition}</p>
                    )}
                    <p className="mt-1 text-sm font-semibold tabular-nums text-gray-900">
                      {r.priceExtracted != null ? PriceFormat(r.priceExtracted) : r.priceRaw ?? "-"}
                    </p>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
